//@flow
/*global chrome*/

import jwtDecode from "jwt-decode"
import { setCookie, getCookie } from "react-simple-cookie-store"
import { callAPI, KIOSK_LOGIN_ROUTE, REFRESH_ROUTE } from "../API"
import { ACCESS_TOKEN, REFRESH_TOKEN, CURRENT_KIOSK_ID } from "./constants"

const isChromeKiosk = typeof chrome === "object"
const kioskCookieStoreHack = { [ACCESS_TOKEN]: "", [REFRESH_TOKEN]: "" }

/**
 * Requests a fresh set of access tokens against the API.
 * @param {string} refreshToken The refresh token to authenticate with.
 * @returns JSON response from the API request.
 */
export async function refreshTokens(refreshToken: string): any {
  try {
    const body = await callAPI(REFRESH_ROUTE, {
      token: refreshToken,
      grantType: "refresh"
    })
    const json = await body.json()
    json && json.data && saveTokens(json.data.token, json.data.refresh)
    return json
  } catch (error) {
    return { error, success: false }
  }
}

/**
 * Requests a fresh set of access tokens against the API for an assigned Kiosk.
 * @param {string} refreshToken The optional refresh token to authenticate with.
 * @returns JSON response from the API request.
 */
export async function refreshTokensForKiosk(
  kioskId: string,
  existingRefreshToken: string
): any {
  try {
    const body = await callAPI(KIOSK_LOGIN_ROUTE, {
      token: existingRefreshToken,
      kioskId
    })
    const json = await body.json()
    json && json.data && saveTokens(json.data.token, json.data.refresh)
    return json
  } catch (error) {
    return { error, success: false }
  }
}

/**
 * Stores the current kiosk ID in a cookie or chrome local storage if applicable.
 * @param {string} kioskId The ID of the kiosk to remember as the default.
 */
export function saveKioskID(kioskId: string): void {
  typeof chrome === "object"
    ? chromeSet(CURRENT_KIOSK_ID, kioskId, 100)
    : setCookie(CURRENT_KIOSK_ID, kioskId, 100)
}

/**
 * A convenience method to obtain the current kiosk ID.
 * @returns {string} The current kiosk ID or a blank string.
 */
export async function currentKioskID(): Promise<string> {
  var kioskId = getCookie(CURRENT_KIOSK_ID)
  // if (isChromeKiosk) {
  //   kioskId = await chromeFetch(CURRENT_KIOSK_ID)
  // }
  if (typeof kioskId === "string") {
    return kioskId
  }
  return ""
}

/**
 * Stores a set of tokens
 * @param {string} accessToken The access token used to make secure requests against the API.
 * @param {string} refreshToken The refresh token used to gain a new access and refresh token combo.
 */
export function saveTokens(accessToken: string, refreshToken: string): void {
  const setter = isChromeKiosk ? chromeSet : setCookie
  setter(ACCESS_TOKEN, accessToken, 7)
  setter(REFRESH_TOKEN, refreshToken, 7)
  kioskCookieStoreHack[ACCESS_TOKEN] = accessToken
  kioskCookieStoreHack[REFRESH_TOKEN] = refreshToken
}

/**
 * A convenience method to obtain the current access token.
 * @returns {string} The current access token or a blank string.
 */
export async function accessToken(): Promise<string> {
  return await getValue(ACCESS_TOKEN)
}

/**
 * Returns an access token value via synchronous methods
 */
export function nonAsyncAccessToken(): string {
  const token = isChromeKiosk
    ? kioskCookieStoreHack[ACCESS_TOKEN]
    : getCookie(ACCESS_TOKEN)
  return typeof token === "string" ? token : ""
}

/**
 * A convenience method to obtain the current refresh token.
 * @returns {string} The current refresh token or a blank string.
 */
export async function refreshToken(): Promise<string> {
  return await getValue(REFRESH_TOKEN)
}

/**
 * Returns a refresh token value via synchronous methods
 */
export function nonAsyncRefreshToken(): string {
  const token = isChromeKiosk
    ? kioskCookieStoreHack[REFRESH_TOKEN]
    : getCookie(REFRESH_TOKEN)
  return typeof token === "string" ? token : ""
}

/**
 * A convenience method to detect if the current user is in fact an
 * admin user based on their JWT access token.
 * @returns {boolean} True if the current user's JWT indicates they are an admin.
 */
export async function isAdmin(): Promise<boolean> {
  return jwtDecode(await accessToken()).admin
}

async function getValue(key: string): Promise<string> {
  var value = getCookie(key)
  // if (isChromeKiosk) {
  //   value = await chromeFetch(key)
  // }
  if (typeof value === "string") {
    return value
  }
  return ""
}

/**
 * Fetches a the value for a supplied key if present in chrome local storage.
 * @param {string} key The key to fetch from local storage.
 * @returns {any} The value for the specified key if present in chrome local storage.
 */
export async function chromeFetch(key: string): any {
  new Promise((resolve, reject) => {
    isChromeKiosk
      ? chrome.storage.local.get([key], function(result) {
          resolve(result[key])
        })
      : resolve(null)
  })
}

/**
 * Stores a key value in Chrome local storage.
 * @param {string} key The key to store in chrome local storage.
 * @param {string} value The value to set for the specified key.
 */
export function chromeSet(key: string, value: string, days: number) {
  setCookie(key, value, 100)
  // isChromeKiosk &&
  //   chrome.storage.local.set({ [key]: value }, function() {
  //     console.log(`Chrome set ${key} with value: ${value}`)
  //   })
}



// WEBPACK FOOTER //
// ./src/Auth/tokenHelpers.js